// app/(app)/invoices/index.tsx
import { Button } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import InvoiceCard from "@/components/invoice/InvoiceCard";
import { useAppTheme } from "@/hooks/useAppTheme";
import { invoiceApi } from "@/services/invoiceApi";
import { locationApi } from "@/services/locationApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { fetchEmployees } from "@/store/slices/employeeSlice";
import { fetchInvoices } from "@/store/slices/invoiceSlice";
import { showAlert } from "@/utils/alert";
import * as FileSystem from "expo-file-system/legacy";
import { router } from "expo-router";
import * as Sharing from "expo-sharing";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Platform,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function InvoicesListScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { invoices, pagination, loading, error } = useAppSelector(
    (state) => state.invoices,
  );
  const { customers } = useAppSelector((state) => state.customers);
  const { employees } = useAppSelector((state) => state.employees);
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = user?.userType === "Owner";
  const isEmployee = user?.userType === "Employee";

  const [refreshing, setRefreshing] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");

  const limit = 10;
  const isInitialLoad = useRef(true);
  const filterTimeout = useRef<number | undefined>(undefined);

  // Load areas
  useEffect(() => {
    loadAreas();
    dispatch(fetchCustomers({ page: 1, limit: 100, search: "" }));
    if (isOwner || isEmployee) {
      dispatch(fetchEmployees({ page: 1, limit: 100, search: "" }));
    }
  }, []);

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Fetch customers when area changes (for customer dropdown)
  useEffect(() => {
    if (selectedAreaId) {
      dispatch(
        fetchCustomers({
          page: 1,
          limit: 100,
          search: "",
          areaId: parseInt(selectedAreaId),
        }),
      );
      setSelectedCustomerId("");
    }
  }, [selectedAreaId]);

  // Debounced customer search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (selectedAreaId) {
        dispatch(
          fetchCustomers({
            page: 1,
            limit: 20,
            search: customerSearch,
            areaId: parseInt(selectedAreaId),
          }),
        );
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [customerSearch, selectedAreaId]);

  // Debounced employee search (only if owner)
  useEffect(() => {
    // if (!isOwner) return;
    const timeout = setTimeout(() => {
      dispatch(fetchEmployees({ page: 1, limit: 20, search: employeeSearch }));
    }, 500);
    return () => clearTimeout(timeout);
  }, [employeeSearch]);

  const loadInvoices = useCallback(
    (page = 1) => {
      const params: any = { page, limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedCustomerId) params.customerId = parseInt(selectedCustomerId);
      if (selectedEmployeeId) params.createdBy = parseInt(selectedEmployeeId);
      if (selectedAreaId) params.areaId = parseInt(selectedAreaId);
      dispatch(fetchInvoices(params));
    },
    [
      startDate,
      endDate,
      selectedCustomerId,
      selectedEmployeeId,
      selectedAreaId,
    ],
  );

  // Debounce filter changes
  useEffect(() => {
    if (isInitialLoad.current) return;
    if (filterTimeout.current) clearTimeout(filterTimeout.current);
    filterTimeout.current = setTimeout(() => {
      loadInvoices(1);
    }, 500);
    return () => clearTimeout(filterTimeout.current);
  }, [
    startDate,
    endDate,
    selectedCustomerId,
    selectedEmployeeId,
    selectedAreaId,
  ]);

  // Initial load
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      loadInvoices(1);
    }
  }, []);

  const loadMore = () => {
    if (!loading && pagination.currentPage < pagination.totalPages) {
      loadInvoices(pagination.currentPage + 1);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadInvoices(1);
    setRefreshing(false);
  };

  const handlePrint = async (invoiceId: number) => {
    try {
      const blob = await invoiceApi.printInvoice(invoiceId);
      if (Platform.OS === "web") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri =
            FileSystem.cacheDirectory + `invoice_${invoiceId}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (err) {
      console.error(err);
      showAlert("Error", "Could not generate PDF");
    }
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedAreaId("");
    setSelectedCustomerId("");
    setSelectedEmployeeId("");
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      padding: spacing.lg,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    title: { fontSize: 24, fontWeight: "600", color: colors.text },
    filterRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.sm,
      marginBottom: spacing.md,
      alignItems: "center",
    },
    filterButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.sm,
      alignItems: "center",
    },
    filterText: { fontSize: 14, color: colors.text },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
  });

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Invoices</Text>
        <View style={{ flexDirection: "row", gap: spacing.sm }}>
          <Button
            title="Bulk PDF"
            onPress={() => router.push("/(app)/invoices/bulk-pdf")}
            variant="outline"
            containerStyle={{ paddingHorizontal: 12, paddingVertical: 8 }}
          />
          <Button
            title="+ New"
            onPress={() => router.push("/(app)/invoices/add")}
            variant="primary"
            containerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}
          />
        </View>
      </View>

      {/* Date Filters */}
      <View style={styles.filterRow}>
        <DatePicker
          value={startDate}
          onChange={setStartDate}
          placeholder="Start Date"
          containerStyle={{ flex: 1, marginBottom: 0 }}
        />
        <DatePicker
          value={endDate}
          onChange={setEndDate}
          placeholder="End Date"
          containerStyle={{ flex: 1, marginBottom: 0 }}
        />
        {(startDate || endDate) && (
          <TouchableOpacity
            style={[styles.filterButton, { borderColor: colors.danger }]}
            onPress={() => {
              setStartDate("");
              setEndDate("");
            }}
          >
            <Text style={{ color: colors.danger }}>Clear</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Area Filter */}
      <View style={styles.filterRow}>
        <SearchableSelect
          label=""
          placeholder="Area"
          options={areas.map((a) => ({
            label: a.name,
            value: a.id.toString(),
          }))}
          value={selectedAreaId}
          onValueChange={setSelectedAreaId}
          containerStyle={{ flex: 1, marginBottom: 0 }}
          clearable
        />
        <SearchableSelect
          label=""
          placeholder="Customer"
          options={customers.map((c) => ({
            label: `${c.fullName} (${c.phone})`,
            value: c.id.toString(),
          }))}
          value={selectedCustomerId}
          onValueChange={setSelectedCustomerId}
          onSearch={setCustomerSearch}
          containerStyle={{ flex: 1, marginBottom: 0 }}
          clearable
          disabled={!selectedAreaId}
        />
      </View>

      {/* Employee Filter – only for Owner */}
      {/* {isOwner && ( */}
      <View style={styles.filterRow}>
        <SearchableSelect
          label=""
          placeholder="Created By (Employee)"
          options={employees.map((emp) => ({
            label: `${emp.ownerName} (${emp.employeeCode})`,
            value: emp.id.toString(),
          }))}
          value={selectedEmployeeId}
          onValueChange={setSelectedEmployeeId}
          onSearch={setEmployeeSearch}
          containerStyle={{ flex: 1, marginBottom: 0 }}
          clearable
        />
      </View>
      {/* )} */}

      {/* Clear all filters */}
      {(startDate ||
        endDate ||
        selectedAreaId ||
        selectedCustomerId ||
        selectedEmployeeId) && (
        <View style={styles.filterRow}>
          <TouchableOpacity
            style={[styles.filterButton, { borderColor: colors.danger }]}
            onPress={clearFilters}
          >
            <Text style={{ color: colors.danger }}>Clear All Filters</Text>
          </TouchableOpacity>
        </View>
      )}

      <FlatList
        data={invoices}
        keyExtractor={(item) => item.id?.toString() || Math.random().toString()}
        renderItem={({ item }) => (
          <InvoiceCard
            invoice={item}
            onRefresh={handleRefresh}
            onPrint={handlePrint}
          />
        )}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        ListEmptyComponent={
          !loading && invoices.length === 0 ? (
            <Text style={styles.emptyText}>No invoices found</Text>
          ) : null
        }
        ListFooterComponent={
          loading ? (
            <ActivityIndicator
              size="small"
              color={colors.primary}
              style={{ marginVertical: spacing.lg }}
            />
          ) : null
        }
      />
    </View>
  );
}
